import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { isNumericFieldError, validateNumericField } from '@/lib/shapes/text-on-path-numeric-field'

/**
 * The typed-input half of a preset + slider + numeric field: the raw input string, the inline
 * out-of-range error AC-6 specifies (rather than a silent clamp), and the shared
 * `validateNumericField` ladder — so every numeric field's accepted values and error copy match.
 *
 * Shared by `NumericPresetField` (Size, Tracking/Kerning) and `TextOnPathOffsetControl`, whose
 * markup stays separate (see the note on that control).
 */
export function useNumericFieldInput(
  value: number,
  min: number,
  max: number,
  onCommit: (next: number) => void
) {
  const [inputValue, setInputValue] = useState(() => String(value))
  const [error, setError] = useState<string | null>(null)
  // While the field has focus the user's raw keystrokes are the source of truth — re-syncing from
  // `value` mid-type would fight the caret and erase a partially typed number like "-" or "1.".
  const isFocusedRef = useRef(false)

  useEffect(() => {
    if (isFocusedRef.current) return
    setInputValue(String(value))
    setError(null)
  }, [value])

  /** A preset or slider pick: always valid, so it also clears any pending error. */
  const commit = (next: number) => {
    setInputValue(String(next))
    setError(null)
    onCommit(next)
  }

  const handleFocus = () => {
    isFocusedRef.current = true
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    setInputValue(raw)
    const result = validateNumericField(raw, min, max)

    if (isNumericFieldError(result)) {
      setError(result.error)
      return
    }

    setError(null)
    onCommit(result.value)
  }

  const handleBlur = () => {
    isFocusedRef.current = false
    // Abandoning an invalid entry restores the value actually in effect, so the field
    // never sits showing a number the canvas is not drawing.
    if (error != null) {
      setInputValue(String(value))
      setError(null)
    }
  }

  return { inputValue, error, commit, handleFocus, handleChange, handleBlur }
}
